import { Spacing } from '@persona-id/design-tokens';
import { capitalize, inRange, snakeCase } from 'lodash/fp';
import { memo } from 'react';
import { Controller } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import {
  DEFAULT_MAX_CHAR_LENGTH,
  DEFAULT_SANITIZE,
  LABEL_VALIDATION_MESSAGE,
  MAX_LENGTH_VALIDATION_MESSAGE,
  PANEL_FOOTER_HEIGHT,
  PANEL_HEADER_HEIGHT,
} from 'dashboard/apps/InquiryTemplateEditor/FieldPanel/shared';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Heading, Paragraph } from 'design-system/components/Text';
import {
  FIELD_POLICY_OPTIONS,
  FIELD_SCHEMA_TYPE_DISPLAY,
  FieldRedactionPolicy,
  FieldSchema,
  FieldSchemaType,
  FieldWritePolicy,
} from 'lib/fields';
import { useFormSecurely } from 'lib/security/reactHookForm';

export const NEW_FIELD_KEY_LABEL =
  "The key for a new field is generated from the label and cannot be changed once it's saved to the template.";

const DEFAULT_FIELD_TYPE: FieldSchemaType = FieldSchemaType.String;
const DEFAULT_REDACTION_POLICY: FieldRedactionPolicy = 'none';
const DEFAULT_WRITE_POLICY: FieldWritePolicy = 'none';

type SupportedAddFieldTypes =
  | FieldSchemaType.Boolean
  | FieldSchemaType.Date
  | FieldSchemaType.Json
  | FieldSchemaType.Number
  | FieldSchemaType.String;

const FIELD_TYPE_OPTIONS: SupportedAddFieldTypes[] = [
  FieldSchemaType.Boolean,
  FieldSchemaType.Date,
  FieldSchemaType.Json,
  FieldSchemaType.Number,
  FieldSchemaType.String,
];

const DEFAULT_VALUE_SUPPORTED_FIELD_TYPES: SupportedAddFieldTypes[] = [
  FieldSchemaType.Boolean,
  FieldSchemaType.Number,
  FieldSchemaType.String,
];

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 400px;
  height: 100%;
  overflow: hidden;
`;

const HeaderContainer = styled.div`
  height: ${PANEL_HEADER_HEIGHT}px;
  padding: ${Spacing[5]};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.secondary};
`;

const BodyContainer = styled.div`
  height: calc(100% - ${PANEL_HEADER_HEIGHT + PANEL_FOOTER_HEIGHT});
  margin-bottom: ${PANEL_FOOTER_HEIGHT}px;
  padding-bottom: ${Spacing[6]};
  overflow-y: auto;
`;

const FormInputs = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${Spacing[3]};
  padding: ${Spacing[5]};
`;

const Wrapper = styled.div`
  margin-bottom: ${Spacing[1]};
`;

const InputLabel = styled(FormControlLabel).attrs(() => ({ size: 'sm' }))`
  font-weight: ${({ theme }) => theme.text.paragraph.md.fontWeight};
`;

const FooterContainer = styled.footer`
  display: flex;
  flex-direction: row;
  align-items: center;
  position: absolute;
  bottom: 0px;
  height: ${PANEL_FOOTER_HEIGHT}px;
  width: 100%;
  background-color: ${({ theme }) => theme.color.background.primary};
  border-top: 1px solid ${({ theme }) => theme.color.border.secondary};
  padding: ${Spacing[5]};
`;

interface AddFieldPanelProps {
  fieldKeys: string[];
  handleAddField: (field: FieldSchema) => void;
  handleClose: () => void;
}

const AddFieldPanel = ({ fieldKeys, handleAddField, handleClose }: AddFieldPanelProps) => {
  const {
    control,
    formState: { errors, isDirty },
    handleSubmit,
    register,
    setValue,
    watch,
  } = useFormSecurely({
    defaultValues: {
      label: '',
      key: '',
      type: DEFAULT_FIELD_TYPE,
      'default-value': null,
      config: {
        'max-char-length': DEFAULT_MAX_CHAR_LENGTH,
        sanitize: DEFAULT_SANITIZE,
        'redaction-policy': DEFAULT_REDACTION_POLICY,
        'write-policy': DEFAULT_WRITE_POLICY,
      },
    },
  });
  const handleSubmitForm = handleSubmit((fieldData: FieldSchema) => handleAddField(fieldData));
  return (
    <Container>
      <HeaderContainer>
        <Heading size='sm'>Add new field</Heading>
      </HeaderContainer>
      <BodyContainer>
        <form onSubmit={handleSubmitForm}>
          <FormInputs>
            <Wrapper>
              <InputLabel htmlFor='form-add-field__name-input'>Name</InputLabel>
              <Controller
                name='label'
                control={control}
                rules={{
                  required: LABEL_VALIDATION_MESSAGE,
                }}
                render={({ field: { value, onChange } }) => (
                  <Input
                    role='input'
                    placeholder='Add a label for your field'
                    data-cy='form-add-field__name-input'
                    id='form-add-field__name-input'
                    value={value}
                    onChange={(e) => {
                      setValue('key', snakeCase(e.target.value));
                      onChange(e.target.value);
                    }}
                    autoFocus
                  />
                )}
              />
              {errors['label'] && <FormError>{errors['label'].message}</FormError>}
            </Wrapper>
            <Wrapper>
              <InputLabel htmlFor='form-add-field__key-input'>Key</InputLabel>
              <Input
                role='input'
                placeholder='--'
                id='form-add-field__key-input'
                {...register('key', {
                  required: 'Field must have a key',
                  validate: (value) =>
                    !fieldKeys.includes(value) ||
                    'A field with this key already exists. Please choose a different key.',
                })}
                disabled={true}
              />
              <Spacer height={1} />
              <Paragraph>{NEW_FIELD_KEY_LABEL}</Paragraph>
              {errors['key'] && <FormError>{errors['key'].message}</FormError>}
            </Wrapper>
            <Wrapper>
              <InputLabel htmlFor='form-add-field__type-select'>Type</InputLabel>
              <Controller
                name='type'
                control={control}
                defaultValue={DEFAULT_FIELD_TYPE}
                render={({ field: { value, onChange } }) => (
                  <Select
                    id='form-add-field__type-select'
                    value={value}
                    onValueChange={(typeValue) => {
                      setValue(
                        'config.max-char-length',
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        typeValue === FieldSchemaType.String ? DEFAULT_MAX_CHAR_LENGTH : null,
                      );
                      setValue(
                        'config.sanitize',
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        typeValue === FieldSchemaType.String ? DEFAULT_SANITIZE : null,
                      );
                      setValue('default-value', null);
                      onChange(typeValue);
                    }}
                  >
                    {FIELD_TYPE_OPTIONS.map((fieldType) => (
                      <Option key={fieldType} value={fieldType}>
                        {FIELD_SCHEMA_TYPE_DISPLAY[fieldType]}
                      </Option>
                    ))}
                  </Select>
                )}
              />
            </Wrapper>
            {DEFAULT_VALUE_SUPPORTED_FIELD_TYPES.includes(watch('type')) && (
              <Wrapper>
                <InputLabel htmlFor='form-add-field__default-value-input'>Default value</InputLabel>
                <Controller
                  name='default-value'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <DefaultValueInput
                      fieldType={watch('type')}
                      value={value}
                      onChange={onChange}
                    />
                  )}
                />
              </Wrapper>
            )}
            {watch('type') === FieldSchemaType.String && (
              <Wrapper>
                <InputLabel htmlFor='form-add-field__max-char-length-input'>
                  Max length (char)
                </InputLabel>
                <Controller
                  name='config.max-char-length'
                  control={control}
                  defaultValue={DEFAULT_MAX_CHAR_LENGTH}
                  rules={{
                    validate: (value) =>
                      inRange(1, DEFAULT_MAX_CHAR_LENGTH + 1, value) ||
                      MAX_LENGTH_VALIDATION_MESSAGE,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <Input
                      type='number'
                      role='input'
                      id='form-add-field__max-char-length-input'
                      value={value}
                      onChange={(e) => {
                        onChange(parseInt(e.target.value));
                      }}
                    />
                  )}
                />
                {errors['config']?.['max-char-length'] && (
                  <FormError>{errors['config']['max-char-length'].message}</FormError>
                )}
              </Wrapper>
            )}
            <Wrapper>
              <InputLabel htmlFor='form-add-field__redaction-policy-select'>
                Redaction policy
              </InputLabel>
              <Controller
                name='config.redaction-policy'
                control={control}
                defaultValue='none'
                render={({ field }) => (
                  <Select id='form-add-field__redaction-policy-select' {...field}>
                    {FIELD_POLICY_OPTIONS['redaction-policy'].map(({ label, value }) => (
                      <Option key={value} value={value}>
                        {label}
                      </Option>
                    ))}
                  </Select>
                )}
              />
            </Wrapper>
            <Wrapper>
              <InputLabel htmlFor='form-add-field__write-policy-select'>Write policy</InputLabel>
              <Controller
                name='config.write-policy'
                control={control}
                defaultValue='none'
                render={({ field }) => (
                  <Select id='form-add-field__write-policy-select' {...field}>
                    {FIELD_POLICY_OPTIONS['write-policy'].map(({ label, value }) => (
                      <Option key={value} value={value}>
                        {label}
                      </Option>
                    ))}
                  </Select>
                )}
              />
            </Wrapper>
          </FormInputs>
          <FooterContainer>
            <HStack equal>
              <Button
                variant='basic'
                aria-label='Close'
                id='add-field-panel__close-button'
                onClick={handleClose}
              >
                Close
              </Button>
              <Button
                type='submit'
                variant='primary'
                aria-label='Add'
                id='add-field-panel__add-button'
                data-cy='add-field-panel__add-button'
                disabled={!isDirty}
                onClick={handleSubmitForm}
              >
                Add
              </Button>
            </HStack>
          </FooterContainer>
        </form>
      </BodyContainer>
    </Container>
  );
};

const DefaultValueInput = ({
  fieldType,
  value,
  onChange,
}: {
  fieldType: SupportedAddFieldTypes;
  value: any;
  onChange: (value: string | number | boolean | null) => void;
}) => {
  const sharedProps = {
    id: 'form-add-field__default-value-input',
    placeholder: 'Set a default value (optional)',
  };
  switch (fieldType) {
    case FieldSchemaType.Boolean: {
      const currentValue = value == null ? '' : value ? 'true' : 'false';
      return (
        <Select
          value={currentValue}
          onValueChange={(val: string) => {
            const newValue = val === '' ? null : val === 'true' ? true : false;
            onChange(newValue);
          }}
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          placeholder='Set a default value (optional)'
          {...sharedProps}
        >
          {['true', 'false'].map((val) => (
            <Option key={val} value={val}>
              {capitalize(val)}
            </Option>
          ))}
        </Select>
      );
    }
    case FieldSchemaType.String:
      return (
        <Input
          role='input'
          type='string'
          value={value}
          onChange={(e) => {
            const defaultValue = e.target.value === '' ? null : e.target.value;
            onChange(defaultValue);
          }}
          {...sharedProps}
        />
      );
    case FieldSchemaType.Number:
      return (
        <Input
          role='input'
          type='number'
          value={value}
          onChange={(e) => {
            const defaultValue = e.target.value === '' ? null : parseInt(e.target.value);
            onChange(defaultValue);
          }}
          {...sharedProps}
        />
      );
    default:
      return null;
  }
};

export default memo(AddFieldPanel);
